import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate, canIssueCertificates, canManageClasses, UserRoles } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Calendar as CalendarIcon, 
  Search, 
  Loader2, 
  Award, 
  CheckCircle, 
  Users,
  Building,
  Filter,
  X,
  GraduationCap,
  Download,
  AlertTriangle,
  FileText,
  Printer,
  Upload,
  FileUp,
  CheckCircle2,
  XCircle,
  Copy
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths } from 'date-fns';
import { SortableHeader } from '../components/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Classes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageTitle('Classes');
  const [classes, setClasses] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [issuingCertificates, setIssuingCertificates] = useState(null);
  const [downloadingLetter, setDownloadingLetter] = useState(null);
  const [letterDialogOpen, setLetterDialogOpen] = useState(false);
  const [letterDialogClass, setLetterDialogClass] = useState(null);
  const [letterDialogCompanies, setLetterDialogCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Historical certificates dialog state
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const [historicalDialogClass, setHistoricalDialogClass] = useState(null);
  const [historicalStudents, setHistoricalStudents] = useState([]);
  const [historicalCertNumbers, setHistoricalCertNumbers] = useState({});
  const [issuingHistorical, setIssuingHistorical] = useState(false);
  
  // Certificate issuance dialog state (for selecting which students to certify)
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certDialogClass, setCertDialogClass] = useState(null);
  const [certDialogStudents, setCertDialogStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentOutcomes, setStudentOutcomes] = useState({}); // student_id -> outcome_name
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;
  
  // Sort state
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Filter states (status can be pre-set via URL, e.g. /classes?status=expired from dashboard widgets)
  const urlStatus = searchParams.get('status');
  const [showFilters, setShowFilters] = useState(!!urlStatus);
  const [trainingCenterFilter, setTrainingCenterFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(urlStatus || 'all');
  const [datePeriod, setDatePeriod] = useState('all');
  const [customDateStart, setCustomDateStart] = useState(null);
  const [customDateEnd, setCustomDateEnd] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteWithCertificates, setDeleteWithCertificates] = useState(false);
  const [certificatesCount, setCertificatesCount] = useState(0);
  
  // Bulk print certificates state
  const [bulkPrintDialogOpen, setBulkPrintDialogOpen] = useState(false);
  const [bulkPrintClass, setBulkPrintClass] = useState(null);
  const [bulkPrintCertificates, setBulkPrintCertificates] = useState([]);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [certificateTemplate, setCertificateTemplate] = useState(null);
  const [declarations, setDeclarations] = useState([]);
  
  // CSV Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchData = async (page = 1, sortByArg = sortBy, sortOrderArg = sortOrder) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (sortByArg) {
        params.append('sort_by', sortByArg);
        params.append('sort_order', sortOrderArg);
      }
      // Server-side filters — the list is paginated, so filtering must happen
      // on the backend (client-side filtering would only see the current page)
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (trainingCenterFilter !== 'all') params.append('training_center_id', trainingCenterFilter);
      if (courseFilter !== 'all') {
        params.append('course_id', courseFilter);
        // Also send the course name so the backend matches orphaned/legacy
        // classes whose course_id no longer matches the live courses doc.
        const cName = courses.find(c => c.id === courseFilter)?.name;
        if (cName) params.append('course_name', cName);
      }
      if (instructorFilter !== 'all') params.append('instructor_id', instructorFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const dateRange = getDateRange(datePeriod, customDateStart, customDateEnd);
      if (dateRange?.start) params.append('date_start', format(dateRange.start, 'yyyy-MM-dd'));
      if (dateRange?.end) params.append('date_end', format(dateRange.end, 'yyyy-MM-dd'));
      
      const classesRes = await axios.get(`${API_URL}/classes?${params}`);
      
      // Handle paginated response
      const classesData = classesRes.data.items || classesRes.data;
      setClasses(classesData);
      setTotalPages(classesRes.data.total_pages || 1);
      setTotalItems(classesRes.data.total || classesData.length);
      setCurrentPage(classesRes.data.page || 1);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Reference data only needs loading once
    (async () => {
      try {
        const [centersRes, instructorsRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/training-centers`),
          axios.get(`${API_URL}/instructors`),
          axios.get(`${API_URL}/courses`),
        ]);
        setTrainingCenters(centersRes.data);
        setInstructors(instructorsRes.data);
        setCourses(coursesRes.data);
      } catch (error) {
        toast.error('Failed to fetch reference data');
      }
    })();
  }, []);

  // Refetch from page 1 whenever a filter changes
  const skipFilterEffect = useRef(true);
  useEffect(() => {
    if (skipFilterEffect.current) { skipFilterEffect.current = false; return; }
    setLoading(true);
    fetchData(1);
  }, [trainingCenterFilter, courseFilter, instructorFilter, statusFilter, datePeriod, customDateStart, customDateEnd]);

  // Debounced server-side search
  const skipSearchEffect = useRef(true);
  useEffect(() => {
    if (skipSearchEffect.current) { skipSearchEffect.current = false; return; }
    const t = setTimeout(() => {
      setLoading(true);
      fetchData(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    fetchData(newPage);
  };

  const handleSort = (field) => {
    let nextBy = field;
    let nextOrder = 'asc';
    if (sortBy === field) {
      if (sortOrder === 'asc') nextOrder = 'desc';
      else { nextBy = null; nextOrder = 'asc'; }
    }
    setSortBy(nextBy);
    setSortOrder(nextOrder);
    setLoading(true);
    fetchData(1, nextBy, nextOrder);
  };

  const handleDuplicate = async (cls) => {
    try {
      const response = await axios.post(`${API_URL}/classes/${cls.id}/duplicate`);
      toast.success('Class duplicated. You can now change the course before issuing certificates.');
      navigate(`/classes/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate class');
    }
  };

  const handleDelete = async (cls) => {
    // Check if class has certificates
    try {
      const response = await axios.get(`${API_URL}/classes/${cls.id}/certificates-count`);
      setCertificatesCount(response.data.count);
    } catch (error) {
      setCertificatesCount(0);
    }
    setDeleteWithCertificates(false);
    setDeleteConfirm(cls);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const cls = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      const url = deleteWithCertificates 
        ? `${API_URL}/classes/${cls.id}?delete_certificates=true`
        : `${API_URL}/classes/${cls.id}`;
      const response = await axios.delete(url);
      toast.success(response.data.message || 'Class deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    } finally {
      setDeleteWithCertificates(false);
      setCertificatesCount(0);
    }
  };

  const handleIssueCertificates = async (cls) => {
    // Lazy-fetch only the persons in this class (not all 10,000)
    let students = [];
    if (cls.student_ids && cls.student_ids.length > 0) {
      try {
        const res = await axios.get(`${API_URL}/persons/lookup?ids=${cls.student_ids.join(',')}`);
        students = res.data.items || [];
      } catch (e) {
        toast.error('Failed to load class students');
        return;
      }
    }

    // Check if this is a historical class
    if (cls.is_historical) {
      // Open dialog for entering custom certificate numbers
      setHistoricalStudents(students);
      setHistoricalDialogClass(cls);
      
      // Initialize certificate numbers (empty)
      const initialNumbers = {};
      students.forEach(s => {
        initialNumbers[s.id] = '';
      });
      setHistoricalCertNumbers(initialNumbers);
      setHistoricalDialogOpen(true);
      return;
    }
    
    // Regular class - open selection dialog
    setCertDialogStudents(students);
    setCertDialogClass(cls);
    setSelectedStudentIds(students.map(s => s.id)); // Select all by default
    // Default each student to the primary course name (no outcome override)
    const course = courses.find(c => c.id === cls.course_id);
    const initialOutcomes = {};
    students.forEach(s => { initialOutcomes[s.id] = course?.name || cls.course_name || ''; });
    setStudentOutcomes(initialOutcomes);
    setCertDialogOpen(true);
  };
  
  const handleConfirmIssueCertificates = async () => {
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    setIssuingCertificates(certDialogClass.id);
    
    try {
      // Build outcome map only for students whose outcome differs from the course name
      const courseName = courses.find(c => c.id === certDialogClass.course_id)?.name || certDialogClass.course_name;
      const outcome_names = {};
      selectedStudentIds.forEach(sid => {
        const chosen = studentOutcomes[sid];
        if (chosen && chosen !== courseName) {
          outcome_names[sid] = chosen;
        }
      });
      
      const response = await axios.post(`${API_URL}/classes/${certDialogClass.id}/issue-certificates`, {
        student_ids: selectedStudentIds,
        ...(Object.keys(outcome_names).length > 0 ? { outcome_names } : {})
      });
      toast.success(response.data.message);
      setCertDialogOpen(false);
      setCertDialogClass(null);
      setCertDialogStudents([]);
      setSelectedStudentIds([]);
      setStudentOutcomes({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue certificates');
    } finally {
      setIssuingCertificates(null);
    }
  };
  
  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };
  
  const toggleAllStudents = () => {
    if (selectedStudentIds.length === certDialogStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(certDialogStudents.map(s => s.id));
    }
  };

  const handleIssueHistoricalCertificates = async () => {
    // Validate all certificate numbers are filled
    const emptyNumbers = Object.entries(historicalCertNumbers).filter(([_, num]) => !num.trim());
    if (emptyNumbers.length > 0) {
      toast.error('Please enter certificate numbers for all students');
      return;
    }
    
    setIssuingHistorical(true);
    
    try {
      const certificates = Object.entries(historicalCertNumbers).map(([personId, certNumber]) => ({
        person_id: personId,
        certificate_number: certNumber.trim()
      }));
      
      const response = await axios.post(
        `${API_URL}/certificates/issue-historical/${historicalDialogClass.id}`,
        { certificates }
      );
      
      toast.success(response.data.message);
      setHistoricalDialogOpen(false);
      setHistoricalDialogClass(null);
      setHistoricalStudents([]);
      setHistoricalCertNumbers({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue historical certificates');
    } finally {
      setIssuingHistorical(false);
    }
  };

  // Open bulk print dialog
  const handleOpenBulkPrint = async (cls) => {
    setBulkPrintClass(cls);
    setBulkPrintDialogOpen(true);
    setLoadingCertificates(true);
    setSelectedCertificates([]);
    
    try {
      // Fetch certificates for this class (server-side filter — the
      // certificates collection is far larger than one page, so client-side
      // filtering of a paginated fetch would miss older classes entirely)
      const certsRes = await axios.get(`${API_URL}/certificates?class_id=${cls.id}&page_size=200`);
      const classCerts = certsRes.data.items || certsRes.data;
      setBulkPrintCertificates(classCerts);
      
      // Select all by default
      setSelectedCertificates(classCerts.map(c => c.id));
      
      // Fetch certificate template if course has one
      const course = courses.find(c => c.id === cls.course_id);
      if (course?.certificate_template_id) {
        const templateRes = await axios.get(`${API_URL}/certificate-templates/${course.certificate_template_id}`);
        setCertificateTemplate(templateRes.data);
      } else {
        setCertificateTemplate(null);
      }
      
      // Fetch declarations
      const declRes = await axios.get(`${API_URL}/declarations`);
      setDeclarations(declRes.data);
    } catch (error) {
      toast.error('Failed to load certificates');
      setBulkPrintDialogOpen(false);
    } finally {
      setLoadingCertificates(false);
    }
  };

  // Toggle certificate selection
  const toggleCertificateSelection = (certId) => {
    setSelectedCertificates(prev => 
      prev.includes(certId) 
        ? prev.filter(id => id !== certId)
        : [...prev, certId]
    );
  };

  // Toggle all certificates
  const toggleAllCertificates = () => {
    if (selectedCertificates.length === bulkPrintCertificates.length) {
      setSelectedCertificates([]);
    } else {
      setSelectedCertificates(bulkPrintCertificates.map(c => c.id));
    }
  };

  // Print selected certificates
  const handleBulkPrint = () => {
    if (selectedCertificates.length === 0) {
      toast.error('Please select at least one certificate to print');
      return;
    }
    
    const selectedCerts = bulkPrintCertificates.filter(c => selectedCertificates.includes(c.id));
    
    // Get class data for the template
    const classData = {
      instructor_names: getCertificateInstructorName(bulkPrintClass.instructor_ids, bulkPrintClass.primary_instructor_id),
      instructor_numbers: getCertificateInstructorNumber(bulkPrintClass.instructor_ids, bulkPrintClass.primary_instructor_id),
      training_center_name: getTrainingCenterName(bulkPrintClass.training_center_id),
      duration_hours: bulkPrintClass.duration_hours,
      declaration_text: declarations.find(d => d.id === bulkPrintClass.declaration_id)?.text,
      extra_text: bulkPrintClass.extra_text,
      signature_url: bulkPrintClass.signature_url
    };
    
    // Open print window with all selected certificates
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      toast.error('Please allow popups to print certificates');
      return;
    }
    
    // Generate HTML for all certificates
    let certificatesHtml = '';
    
    if (certificateTemplate) {
      // Template-based printing
      const orientation = certificateTemplate.page_orientation || 'portrait';
      const pageWidth = orientation === 'landscape' ? 297 : 210;
      const pageHeight = orientation === 'landscape' ? 210 : 297;
      
      const renderField = (fieldConfig, value) => {
        if (!fieldConfig || !fieldConfig.enabled || !value) return '';
        const { x, y, font_size, font_weight, text_align } = fieldConfig;
        return `
          <div style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            font-size: ${font_size || 12}pt;
            font-weight: ${font_weight || 'normal'};
            text-align: ${text_align || 'left'};
            ${text_align === 'center' ? 'transform: translateX(-50%);' : ''}
            ${text_align === 'right' ? 'transform: translateX(-100%);' : ''}
          ">${value}</div>
        `;
      };
      
      const renderImage = (fieldConfig, imageUrl) => {
        if (!fieldConfig || !fieldConfig.enabled || !imageUrl) return '';
        const { x, y, font_size } = fieldConfig;
        const height = font_size ? font_size * 2 : 30;
        return `
          <img src="${imageUrl}" style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            height: ${height}mm;
            object-fit: contain;
          " />
        `;
      };
      
      const signatureFullUrl = classData.signature_url 
        ? window.location.origin + classData.signature_url
        : null;
      
      selectedCerts.forEach((cert, index) => {
        certificatesHtml += `
          <div class="certificate-page" style="page-break-after: ${index < selectedCerts.length - 1 ? 'always' : 'auto'};">
            ${renderField(certificateTemplate.certificate_number, cert.certificate_number)}
            ${renderField(certificateTemplate.student_name, cert.person_name)}
            ${renderField(certificateTemplate.course_name, cert.course_name)}
            ${renderField(certificateTemplate.company_name, cert.company_name)}
            ${renderField(certificateTemplate.issue_date, new Date(cert.issue_date).toLocaleDateString('en-GB'))}
            ${renderField(certificateTemplate.expiry_date, cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString('en-GB') : null)}
            ${renderField(certificateTemplate.instructor_names, classData.instructor_names)}
            ${renderField(certificateTemplate.instructor_numbers, classData.instructor_numbers)}
            ${renderField(certificateTemplate.training_center_name, classData.training_center_name)}
            ${renderField(certificateTemplate.duration_hours, classData.duration_hours ? `${classData.duration_hours} hours` : null)}
            ${renderField(certificateTemplate.declaration_text, classData.declaration_text)}
            ${renderField(certificateTemplate.extra_text, classData.extra_text)}
            ${renderImage(certificateTemplate.signature, signatureFullUrl)}
          </div>
        `;
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificates - ${bulkPrintClass.course_name}</title>
            <style>
              @page {
                size: ${orientation};
                margin: 0;
              }
              body {
                font-family: 'Inter', Arial, sans-serif;
                margin: 0;
                padding: 0;
              }
              .certificate-page {
                position: relative;
                width: ${pageWidth}mm;
                height: ${pageHeight}mm;
                overflow: hidden;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            ${certificatesHtml}
          </body>
        </html>
      `);
    } else {
      // Default layout printing
      selectedCerts.forEach((cert, index) => {
        certificatesHtml += `
          <div class="certificate-page" style="page-break-after: ${index < selectedCerts.length - 1 ? 'always' : 'auto'};">
            <div class="certificate-header">
              <img src="${window.location.origin}/logo.png" alt="EMP Medic" />
              <p style="color: #666; margin-top: 10px;">Training Certificate</p>
            </div>
            <div class="certificate-field">
              <label>Certificate Number</label>
              <p class="certificate-number">${cert.certificate_number}</p>
            </div>
            <div class="certificate-field">
              <label>Participant Name</label>
              <p>${cert.person_name}</p>
            </div>
            <div class="certificate-field">
              <label>Course</label>
              <p>${cert.course_name}</p>
            </div>
            ${cert.company_name ? `
            <div class="certificate-field">
              <label>Company</label>
              <p>${cert.company_name}</p>
            </div>
            ` : ''}
            ${classData.instructor_names && classData.instructor_names !== '-' ? `
            <div class="certificate-field">
              <label>Instructor(s)</label>
              <p>${classData.instructor_names}</p>
            </div>
            ` : ''}
            ${classData.declaration_text ? `
            <div class="certificate-field">
              <label>Declaration</label>
              <p>${classData.declaration_text}</p>
            </div>
            ` : ''}
            ${classData.extra_text ? `
            <div class="certificate-field">
              <label>Additional Information</label>
              <p>${classData.extra_text}</p>
            </div>
            ` : ''}
            <div style="display: flex; gap: 40px;">
              <div class="certificate-field" style="flex: 1;">
                <label>Issue Date</label>
                <p>${new Date(cert.issue_date).toLocaleDateString('en-GB')}</p>
              </div>
              ${cert.expiry_date ? `
              <div class="certificate-field" style="flex: 1;">
                <label>Expiry Date</label>
                <p>${new Date(cert.expiry_date).toLocaleDateString('en-GB')}</p>
              </div>
              ` : ''}
            </div>
            ${classData.signature_url ? `
            <div class="certificate-field" style="margin-top: 30px;">
              <label>Signature</label>
              <img src="${window.location.origin}${classData.signature_url}" style="max-height: 60px; margin-top: 10px;" />
            </div>
            ` : ''}
          </div>
        `;
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificates - ${bulkPrintClass.course_name}</title>
            <style>
              body {
                font-family: 'Inter', sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              .certificate-page {
                padding: 40px 0;
              }
              .certificate-header {
                text-align: center;
                margin-bottom: 40px;
                padding-bottom: 20px;
                border-bottom: 2px solid #2C9DE1;
              }
              .certificate-header img {
                max-height: 60px;
              }
              .certificate-field {
                margin: 20px 0;
                padding: 10px 0;
                border-bottom: 1px solid #e5e5e5;
              }
              .certificate-field label {
                font-weight: 600;
                color: #666;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .certificate-field p {
                font-size: 18px;
                margin: 5px 0 0 0;
                color: #111;
              }
              .certificate-number {
                font-family: monospace;
                font-size: 24px;
                color: #2C9DE1;
              }
              @media print {
                body { padding: 20px; }
                .certificate-page { padding: 20px 0; }
              }
            </style>
          </head>
          <body>
            ${certificatesHtml}
          </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
    
    toast.success(`Printing ${selectedCerts.length} certificate(s)`);
  };

  // CSV Import handler
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API_URL}/classes/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported} records`);
        fetchData(); // Refresh the classes list
      } else {
        toast.warning(`Imported ${response.data.imported} records with ${response.data.errors.length} errors`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import CSV';
      toast.error(errorMsg);
      setImportResult({
        success: false,
        total_rows: 0,
        imported: 0,
        errors: [errorMsg],
        classes_created: 0,
        students_created: 0,
        certificates_created: 0
      });
    } finally {
      setImporting(false);
    }
  };

  const getTrainingCenterName = (centerId) => {
    const center = trainingCenters.find(c => c.id === centerId);
    return center?.name || '-';
  };

  const getInstructorNames = (instructorIds, primaryInstructorId = null) => {
    if (!instructorIds || instructorIds.length === 0) return '-';
    // Sort: primary first, then others in original order
    const sortedIds = primaryInstructorId && instructorIds.includes(primaryInstructorId)
      ? [primaryInstructorId, ...instructorIds.filter(id => id !== primaryInstructorId)]
      : instructorIds;
    return sortedIds.map(id => {
      const instructor = instructors.find(i => i.id === id);
      return instructor ? `${instructor.first_name} ${instructor.last_name}` : null;
    }).filter(Boolean).join(', ') || '-';
  };

  // Get only the primary instructor name (or first if none set) for certificate rendering
  const getCertificateInstructorName = (instructorIds, primaryInstructorId = null) => {
    if (!instructorIds || instructorIds.length === 0) return '';
    const id = primaryInstructorId && instructorIds.includes(primaryInstructorId)
      ? primaryInstructorId
      : instructorIds[0];
    const instructor = instructors.find(i => i.id === id);
    return instructor ? `${instructor.first_name} ${instructor.last_name}` : '';
  };

  const getCertificateInstructorNumber = (instructorIds, primaryInstructorId = null) => {
    if (!instructorIds || instructorIds.length === 0) return '';
    const id = primaryInstructorId && instructorIds.includes(primaryInstructorId)
      ? primaryInstructorId
      : instructorIds[0];
    const instructor = instructors.find(i => i.id === id);
    return instructor?.instructor_number || '';
  };

  // Get date range based on period preset
  const getDateRange = (period, customStart, customEnd) => {
    const now = new Date();
    switch (period) {
      case 'this_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'this_year':
        return { start: startOfYear(now), end: endOfYear(now) };
      case 'last_year':
        const lastYear = new Date(now.getFullYear() - 1, 0, 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      case 'custom':
        return { start: customStart, end: customEnd };
      default:
        return null;
    }
  };

  // Classes are filtered server-side (see fetchData) — the API returns exactly
  // the rows for the current page of the active filters.
  const filteredClasses = classes;

  // Count active filters
  const activeFilterCount = [
    trainingCenterFilter !== 'all',
    courseFilter !== 'all',
    instructorFilter !== 'all',
    statusFilter !== 'all',
    datePeriod !== 'all'
  ].filter(Boolean).length;

  const clearFilters = () => {
    setTrainingCenterFilter('all');
    setCourseFilter('all');
    setInstructorFilter('all');
    setStatusFilter('all');
    setDatePeriod('all');
    setCustomDateStart(null);
    setCustomDateEnd(null);
  };

  // Export classes to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();
      
      if (trainingCenterFilter !== 'all') {
        params.append('training_center_id', trainingCenterFilter);
      }
      if (courseFilter !== 'all') {
        params.append('course_id', courseFilter);
        // Also send the course name so the backend can match orphaned/legacy
        // classes whose course_id no longer matches the live courses doc.
        const cName = courses.find(c => c.id === courseFilter)?.name;
        if (cName) params.append('course_name', cName);
      }
      
      // Get date range for export
      const dateRange = getDateRange(datePeriod, customDateStart, customDateEnd);
      if (dateRange && dateRange.start) {
        params.append('date_start', format(dateRange.start, 'yyyy-MM-dd'));
      }
      if (dateRange && dateRange.end) {
        params.append('date_end', format(dateRange.end, 'yyyy-MM-dd'));
      }
      
      const response = await axios.get(`${API_URL}/classes/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'classes_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filteredClasses.length} classes to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export classes');
    } finally {
      setExporting(false);
    }
  };

  // Open dialog for Multi class confirmation letters
  const openConfirmationLetterDialog = async (cls) => {
    setLetterDialogClass(cls);
    setLetterDialogOpen(true);
    setLoadingCompanies(true);
    
    try {
      const response = await axios.get(`${API_URL}/classes/${cls.id}/confirmation-letter-companies`);
      setLetterDialogCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
      setLetterDialogCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Download confirmation letter PDF
  const handleDownloadConfirmationLetter = async (cls, companyId = null) => {
    const downloadKey = companyId ? `${cls.id}-${companyId}` : cls.id;
    setDownloadingLetter(downloadKey);
    
    try {
      let url = `${API_URL}/classes/${cls.id}/confirmation-letter`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Create download link
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'confirmation_letter.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Confirmation letter downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download confirmation letter');
    } finally {
      setDownloadingLetter(null);
    }
  };

  // Download Class Confirmation PDF (addressed to Training Center)
  const handleDownloadClassConfirmation = async (cls) => {
    const downloadKey = `class-${cls.id}`;
    setDownloadingLetter(downloadKey);
    
    try {
      const response = await axios.get(`${API_URL}/classes/${cls.id}/class-confirmation`, { responseType: 'blob' });
      
      // Create download link
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'class_confirmation.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Class confirmation downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download class confirmation');
    } finally {
      setDownloadingLetter(null);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Classes
          </h1>
          <p className="text-slate-500 mt-1">
            Manage training classes and issue certificates
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === UserRoles.ADMIN && (
            <Button 
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting || filteredClasses.length === 0}
              data-testid="export-classes-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
          {(user?.role === UserRoles.ADMIN || user?.role === UserRoles.TRAINING_ADMIN) && (
            <Button 
              variant="outline"
              onClick={() => {
                setImportDialogOpen(true);
                setImportFile(null);
                setImportResult(null);
              }}
              data-testid="import-classes-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
          )}
          {canManageClasses(user?.role) && (
            <Button 
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={() => navigate('/classes/new')}
              data-testid="add-class-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Class
            </Button>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by course, location, instructor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="class-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={activeFilterCount > 0 ? 'border-[#2C9DE1] text-[#2C9DE1]' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-[#2C9DE1] text-white">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Filter Classes</h3>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Training Center Filter */}
                <div className="space-y-2">
                  <Label>Training Center</Label>
                  <Select value={trainingCenterFilter} onValueChange={setTrainingCenterFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Centers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Centers</SelectItem>
                      {trainingCenters.map((center) => (
                        <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Course Filter */}
                <div className="space-y-2">
                  <Label>Course</Label>
                  <Select value={courseFilter} onValueChange={setCourseFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Instructor Filter */}
                <div className="space-y-2">
                  <Label>Instructor</Label>
                  <Select value={instructorFilter} onValueChange={setInstructorFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Instructors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Instructors</SelectItem>
                      {instructors.map((instructor) => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.first_name} {instructor.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="certified">Certified</SelectItem>
                      <SelectItem value="expiring">&lt;90 to Expire</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Filter */}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Select value={datePeriod} onValueChange={setDatePeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="this_year">This Year</SelectItem>
                      <SelectItem value="last_year">Last Year</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  {datePeriod === 'custom' && (
                    <div className="flex gap-2 mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customDateStart ? format(customDateStart, 'dd MMM yyyy') : 'Start'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customDateStart} onSelect={setCustomDateStart} />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customDateEnd ? format(customDateEnd, 'dd MMM yyyy') : 'End'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customDateEnd} onSelect={setCustomDateEnd} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </div>

              {/* Results count */}
              <div className="text-sm text-slate-500">
                Showing {classes.length} of {totalItems} matching classes
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Classes Table */}
      <Card>
        <CardContent className="p-0">
          {filteredClasses.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No classes found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm || activeFilterCount > 0 ? 'Try adjusting your filters' : 'Get started by creating your first class'}
              </p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
              {!searchTerm && activeFilterCount === 0 && canManageClasses(user?.role) && (
                <Button 
                  className="mt-4 bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                  onClick={() => navigate('/classes/new')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Class
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHeader field="date_completed" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-class-completion">Completion Date</SortableHeader>
                  <SortableHeader field="class_type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-class-type">Type</SortableHeader>
                  <SortableHeader field="course_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-class-course">Course</SortableHeader>
                  <TableHead className="font-semibold">Instructor(s)</TableHead>
                  <TableHead className="font-semibold">Training Center</TableHead>
                  <TableHead className="font-semibold">Students</TableHead>
                  <SortableHeader field="filing_number" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-class-filing">Filing #</SortableHeader>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClasses.map((cls) => (
                  <TableRow key={cls.id} className="hover:bg-slate-50/50" data-testid={`class-row-${cls.id}`}>
                    <TableCell 
                      className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                      onClick={() => navigate(`/classes/${cls.id}/edit`)}
                    >
                      {formatDate(cls.date_completed || cls.date_start)}
                    </TableCell>
                    <TableCell>
                      {cls.class_type === 'inhouse' ? (
                        <span className="text-[#2C9DE1] font-medium" title={cls.company_name || 'InHouse'}>
                          {cls.company_name || 'InHouse'}
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Multi</Badge>
                      )}
                    </TableCell>
                    <TableCell 
                      className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                      onClick={() => navigate(`/classes/${cls.id}/edit`)}
                    >
                      <div className="flex items-center gap-2">
                        {cls.course_name}
                        {cls.is_historical && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                            Historical
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <GraduationCap className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate max-w-[150px]" title={getInstructorNames(cls.instructor_ids, cls.primary_instructor_id)}>
                          {getInstructorNames(cls.instructor_ids, cls.primary_instructor_id)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Building className="w-4 h-4" />
                        {getTrainingCenterName(cls.training_center_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-slate-400" />
                        {cls.student_ids?.length || 0}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">
                      {cls.filing_number || '-'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (cls.certificates_issued) {
                          // Calculate if certificates have expired based on completion date and course expiration
                          const course = courses.find(c => c.id === cls.course_id);
                          const completionDate = cls.date_completed || cls.date_start;
                          if (course && completionDate) {
                            const expiryDate = new Date(completionDate);
                            expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
                            const now = new Date();
                            
                            if (expiryDate < now) {
                              return (
                                <Badge className="bg-red-100 text-red-800">
                                  <X className="w-3 h-3 mr-1" />
                                  Expired
                                </Badge>
                              );
                            } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
                              // Expiring within 90 days
                              return (
                                <Badge className="bg-amber-100 text-amber-800">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  &lt;90 to Expire
                                </Badge>
                              );
                            }
                          }
                          return (
                            <Badge className="bg-emerald-100 text-emerald-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Certified
                            </Badge>
                          );
                        } else if (cls.date_completed) {
                          return <Badge className="bg-amber-100 text-amber-800">Completed</Badge>;
                        } else {
                          return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`class-actions-${cls.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canIssueCertificates(user?.role) && !cls.certificates_issued && cls.student_ids?.length > 0 && (
                            <DropdownMenuItem 
                              onClick={() => handleIssueCertificates(cls)}
                              disabled={issuingCertificates === cls.id}
                            >
                              {issuingCertificates === cls.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Award className="w-4 h-4 mr-2" />
                              )}
                              Issue Certificates
                            </DropdownMenuItem>
                          )}
                          {/* Confirmation Letter Download - only for Admin/Training Admin after certificates issued */}
                          {canIssueCertificates(user?.role) && cls.certificates_issued && (
                            cls.class_type === 'inhouse' ? (
                              <DropdownMenuItem 
                                onClick={() => handleDownloadConfirmationLetter(cls)}
                                disabled={downloadingLetter === cls.id}
                              >
                                {downloadingLetter === cls.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <FileText className="w-4 h-4 mr-2" />
                                )}
                                Confirmation Letter
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => openConfirmationLetterDialog(cls)}>
                                <FileText className="w-4 h-4 mr-2" />
                                Confirmation Letters
                              </DropdownMenuItem>
                            )
                          )}
                          {/* Class Confirmation - addressed to Training Center */}
                          {canIssueCertificates(user?.role) && cls.certificates_issued && (
                            <DropdownMenuItem 
                              onClick={() => handleDownloadClassConfirmation(cls)}
                              disabled={downloadingLetter === `class-${cls.id}`}
                            >
                              {downloadingLetter === `class-${cls.id}` ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4 mr-2" />
                              )}
                              Class Confirmation
                            </DropdownMenuItem>
                          )}
                          {/* Print Certificates - only after certificates issued */}
                          {cls.certificates_issued && (
                            <DropdownMenuItem onClick={() => handleOpenBulkPrint(cls)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Print Certificates
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDuplicate(cls)} data-testid={`class-duplicate-${cls.id}`}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate Class
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/classes/${cls.id}/edit`)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(cls)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} classes
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Letter Dialog for Multi Classes */}
      <Dialog open={letterDialogOpen} onOpenChange={setLetterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download Confirmation Letters</DialogTitle>
            <DialogDescription>
              {letterDialogClass && (
                <>Select a company to download the confirmation letter for <strong>{letterDialogClass.course_name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {loadingCompanies ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#2C9DE1]" />
              </div>
            ) : letterDialogCompanies.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No companies found with certified students</p>
            ) : (
              letterDialogCompanies.map(company => (
                <Button
                  key={company.company_id}
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => handleDownloadConfirmationLetter(letterDialogClass, company.company_id)}
                  disabled={downloadingLetter === `${letterDialogClass?.id}-${company.company_id}`}
                >
                  <span className="flex items-center gap-2">
                    {downloadingLetter === `${letterDialogClass?.id}-${company.company_id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    {company.company_name}
                  </span>
                  <Badge variant="secondary">{company.student_count} student{company.student_count !== 1 ? 's' : ''}</Badge>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => {
          setDeleteConfirm(null);
          setDeleteWithCertificates(false);
          setCertificatesCount(0);
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Class
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this class?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-3 bg-slate-50 rounded-lg mb-4">
                <p className="font-medium text-slate-900">{deleteConfirm.course_name}</p>
                <p className="text-sm text-slate-500">
                  {deleteConfirm.date_completed ? formatDate(deleteConfirm.date_completed) : 'No completion date'}
                </p>
              </div>
              
              {certificatesCount > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        This class has {certificatesCount} certificate{certificatesCount !== 1 ? 's' : ''} issued
                      </p>
                      <div className="mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={deleteWithCertificates}
                            onChange={(e) => setDeleteWithCertificates(e.target.checked)}
                            className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-700">
                            Also delete the {certificatesCount} certificate{certificatesCount !== 1 ? 's' : ''}
                          </span>
                        </label>
                      </div>
                      <p className="text-xs text-amber-600 mt-2">
                        Note: Students and companies will not be deleted.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {certificatesCount === 0 && (
                <p className="text-sm text-slate-500">
                  This class has no certificates issued.
                </p>
              )}
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteConfirm(null);
                  setDeleteWithCertificates(false);
                  setCertificatesCount(0);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete{deleteWithCertificates ? ' with Certificates' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Historical Certificates Dialog */}
      <Dialog open={historicalDialogOpen} onOpenChange={setHistoricalDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Issue Historical Certificates
            </DialogTitle>
            <DialogDescription>
              Enter the original certificate numbers for each student. These numbers will be used instead of auto-generated ones.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {historicalDialogClass && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Historical Import</span>
                </div>
                <p className="text-sm text-amber-700 mt-1">
                  Course: <strong>{historicalDialogClass.course_name}</strong> • 
                  Date: <strong>{formatDate(historicalDialogClass.date_start)}</strong>
                </p>
              </div>
            )}
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student Name</TableHead>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Certificate Number *</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historicalStudents.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell className="font-medium">
                      {student.first_name} {student.last_name}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {student.id_number || '-'}
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="e.g., EFW00123"
                        value={historicalCertNumbers[student.id] || ''}
                        onChange={(e) => setHistoricalCertNumbers({
                          ...historicalCertNumbers,
                          [student.id]: e.target.value
                        })}
                        className="w-40"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setHistoricalDialogOpen(false);
                setHistoricalDialogClass(null);
                setHistoricalStudents([]);
                setHistoricalCertNumbers({});
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={handleIssueHistoricalCertificates}
              disabled={issuingHistorical}
            >
              {issuingHistorical ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Issuing...
                </>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-2" />
                  Issue Certificates
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate Selection Dialog - Select which students to certify */}
      <Dialog open={certDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCertDialogOpen(false);
          setCertDialogClass(null);
          setCertDialogStudents([]);
          setSelectedStudentIds([]);
          setStudentOutcomes({});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Issue Certificates
            </DialogTitle>
            <DialogDescription>
              Select the students who should receive certificates. Uncheck any students who participated but should not be certified.
            </DialogDescription>
          </DialogHeader>
          
          {(() => {
            const courseForDialog = certDialogClass ? courses.find(c => c.id === certDialogClass.course_id) : null;
            const courseName = courseForDialog?.name || certDialogClass?.course_name || '';
            const outcomes = courseForDialog?.outcomes || [];
            const hasAlternates = outcomes.length > 0;
            const allOutcomeOptions = hasAlternates ? [courseName, ...outcomes] : [];
            
            return (
              <div className="space-y-4 py-4">
                {certDialogClass && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Course: <strong>{certDialogClass.course_name}</strong> • 
                      Date: <strong>{formatDate(certDialogClass.date_start)}</strong>
                    </p>
                    {hasAlternates && (
                      <p className="text-xs text-blue-700 mt-1">
                        This course has alternate outcomes — pick the certificate name per student. Defaults to <strong>{courseName}</strong>.
                      </p>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">
                    {selectedStudentIds.length} of {certDialogStudents.length} students selected
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleAllStudents}
                  >
                    {selectedStudentIds.length === certDialogStudents.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.length === certDialogStudents.length && certDialogStudents.length > 0}
                          onChange={toggleAllStudents}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </TableHead>
                      <TableHead>Student Name</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Company</TableHead>
                      {hasAlternates && <TableHead>Outcome</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certDialogStudents.map((student) => (
                      <TableRow 
                        key={student.id}
                        className={`cursor-pointer ${selectedStudentIds.includes(student.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleStudentSelection(student.id)}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedStudentIds.includes(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {student.id_number || '-'}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {student.company_name || '-'}
                        </TableCell>
                        {hasAlternates && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={studentOutcomes[student.id] || courseName}
                              onValueChange={(val) => setStudentOutcomes(prev => ({ ...prev, [student.id]: val }))}
                              disabled={!selectedStudentIds.includes(student.id)}
                            >
                              <SelectTrigger className="w-[180px]" data-testid={`student-outcome-${student.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {allOutcomeOptions.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })()}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCertDialogOpen(false);
                setCertDialogClass(null);
                setCertDialogStudents([]);
                setSelectedStudentIds([]);
                setStudentOutcomes({});
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={handleConfirmIssueCertificates}
              disabled={issuingCertificates || selectedStudentIds.length === 0}
            >
              {issuingCertificates ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Issuing...
                </>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-2" />
                  Issue {selectedStudentIds.length} Certificate{selectedStudentIds.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Print Certificates Dialog */}
      <Dialog open={bulkPrintDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setBulkPrintDialogOpen(false);
          setBulkPrintClass(null);
          setBulkPrintCertificates([]);
          setSelectedCertificates([]);
          setCertificateTemplate(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Print Certificates
            </DialogTitle>
            <DialogDescription>
              {bulkPrintClass && (
                <>
                  Select which certificates to print for <strong>{bulkPrintClass.course_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingCertificates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#2C9DE1]" />
              <span className="ml-2 text-slate-500">Loading certificates...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All Header */}
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedCertificates.length === bulkPrintCertificates.length && bulkPrintCertificates.length > 0}
                    onCheckedChange={toggleAllCertificates}
                  />
                  <Label htmlFor="select-all" className="cursor-pointer font-medium">
                    Select All ({bulkPrintCertificates.length})
                  </Label>
                </div>
                <span className="text-sm text-slate-500">
                  {selectedCertificates.length} selected
                </span>
              </div>
              
              {/* Certificates List */}
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="font-semibold">Student Name</TableHead>
                      <TableHead className="font-semibold">Certificate #</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkPrintCertificates.map((cert) => (
                      <TableRow 
                        key={cert.id} 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleCertificateSelection(cert.id)}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedCertificates.includes(cert.id)}
                            onCheckedChange={() => toggleCertificateSelection(cert.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {cert.person_name}
                        </TableCell>
                        <TableCell className="font-mono text-[#2C9DE1]">
                          {cert.certificate_number}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {cert.company_name || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Template Info */}
              {certificateTemplate && (
                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-700 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Using template: <strong>{certificateTemplate.name}</strong> ({certificateTemplate.page_orientation})
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setBulkPrintDialogOpen(false);
                setBulkPrintClass(null);
                setBulkPrintCertificates([]);
                setSelectedCertificates([]);
                setCertificateTemplate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={handleBulkPrint}
              disabled={selectedCertificates.length === 0}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Selected ({selectedCertificates.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        if (!open && !importing) {
          setImportDialogOpen(false);
          setImportFile(null);
          setImportResult(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Import Classes from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to import historical classes with certificates.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* CSV Format Info */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Expected CSV Columns:</p>
              <p className="text-xs text-blue-700 font-mono">
                first name, last name, certificate number, company, course, date of completion, city, country, instructor, id number, tel, position, email, training center, declaration
              </p>
              <p className="text-xs text-blue-600 mt-2">
                <strong>Required:</strong> first name, last name, certificate number, course, date of completion
              </p>
              <p className="text-xs text-blue-600 mt-1">
                <strong>Optional:</strong> training center (matched by name; defaults to first center if blank/not found), declaration (matched by title)
              </p>
            </div>
            
            {/* File Upload */}
            {!importResult && (
              <div className="space-y-3">
                <Label>Select CSV File</Label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                    ${importFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-[#2C9DE1] hover:bg-slate-50'}`}
                  onClick={() => document.getElementById('csv-file-input').click()}
                >
                  <input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    disabled={importing}
                  />
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2 text-emerald-700">
                      <FileUp className="w-6 h-6" />
                      <span className="font-medium">{importFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p>Click to select a CSV file</p>
                      <p className="text-xs mt-1">or drag and drop</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Import Results */}
            {importResult && (
              <div className="space-y-4">
                {/* Summary */}
                <div className={`p-4 rounded-lg border ${importResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {importResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={`font-medium ${importResult.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {importResult.success ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="bg-white/50 p-2 rounded">
                      <p className="text-slate-500 text-xs">Total Rows</p>
                      <p className="font-semibold">{importResult.total_rows}</p>
                    </div>
                    <div className="bg-white/50 p-2 rounded">
                      <p className="text-slate-500 text-xs">Imported</p>
                      <p className="font-semibold text-emerald-600">{importResult.imported}</p>
                    </div>
                    <div className="bg-white/50 p-2 rounded">
                      <p className="text-slate-500 text-xs">Classes</p>
                      <p className="font-semibold">{importResult.classes_created}</p>
                    </div>
                    <div className="bg-white/50 p-2 rounded">
                      <p className="text-slate-500 text-xs">Students</p>
                      <p className="font-semibold">{importResult.students_created}</p>
                    </div>
                    <div className="bg-white/50 p-2 rounded">
                      <p className="text-slate-500 text-xs">Companies</p>
                      <p className="font-semibold">{importResult.companies_created || 0}</p>
                    </div>
                  </div>
                </div>
                
                {/* Errors */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-red-700 flex items-center gap-1">
                      <XCircle className="w-4 h-4" />
                      Errors ({importResult.errors.length})
                    </p>
                    <div className="max-h-48 overflow-y-auto bg-red-50 rounded border border-red-200 p-3">
                      {importResult.errors.map((error, idx) => (
                        <p key={idx} className="text-xs text-red-700 py-1 border-b border-red-100 last:border-0">
                          {error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
                setImportResult(null);
              }}
              disabled={importing}
            >
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                onClick={handleImportCSV}
                disabled={!importFile || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            )}
            {importResult && !importResult.success && (
              <Button
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                onClick={() => {
                  setImportFile(null);
                  setImportResult(null);
                }}
              >
                Try Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Classes;
